import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

const CampContext = createContext(null);

function applyTheme(branding = {}) {
  const root = document.documentElement;
  if (branding.primary_color) {
    root.style.setProperty('--camp-primary', branding.primary_color);
    // Generate dark variant (darken by ~15%)
    root.style.setProperty('--camp-primary-dark', darken(branding.primary_color, 0.15));
    root.style.setProperty('--camp-primary-rgb', hexToRgb(branding.primary_color));
  } else {
    root.style.removeProperty('--camp-primary');
    root.style.removeProperty('--camp-primary-dark');
    root.style.removeProperty('--camp-primary-rgb');
  }
  if (branding.secondary_color) {
    root.style.setProperty('--camp-secondary', branding.secondary_color);
    root.style.setProperty('--camp-secondary-rgb', hexToRgb(branding.secondary_color));
  } else {
    root.style.removeProperty('--camp-secondary');
    root.style.removeProperty('--camp-secondary-rgb');
  }
  if (branding.accent_color) {
    root.style.setProperty('--camp-accent', branding.accent_color);
  } else {
    root.style.removeProperty('--camp-accent');
  }
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

function darken(hex, amount) {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) * (1 - amount));
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) * (1 - amount));
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) * (1 - amount));
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export function CampProvider({ children }) {
  const { campSlug } = useParams();
  const { user, simulatedUser } = useAuth();
  const [camp, setCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState({});
  const [tenant, setTenant] = useState(null);
  const [allCampsInTenant, setAllCampsInTenant] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState(null);

  const loadCamp = useCallback(async () => {
    if (!campSlug) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`/api/${campSlug}/info`);
      setCamp(data);
      setModules(data.modules || {});
      setTenant(data.tenant || null);
      setAllCampsInTenant(data.allCampsInTenant || []);
      applyTheme(data.branding || {});
    } catch (e) {
      setError(e.response?.data?.error || 'Camp nicht gefunden');
    } finally {
      setLoading(false);
    }
  }, [campSlug]);

  const loadPermissions = useCallback(async () => {
    if (!campSlug || !user) return;
    try {
      const { data } = await axios.get(`/api/${campSlug}/roles/my-permissions`);
      setEffectivePermissions(data.effectivePermissions || {});
    } catch {
      setEffectivePermissions({});
    }
  }, [campSlug, user]);

  useEffect(() => { loadCamp(); }, [loadCamp]);
  useEffect(() => { loadPermissions(); }, [loadPermissions]);

  // Reset theme when leaving camp context
  useEffect(() => {
    return () => applyTheme({});
  }, [campSlug]);

  const isModuleEnabled = (key) => modules[key] !== false;
  const isActiveCamp = camp?.is_active !== false;
  const refreshCamp = loadCamp;

  const hasPermission = useCallback((key) => {
    // Simulation takes priority — even superadmins see restricted view when simulating
    if (simulatedUser) return simulatedUser.effectivePermissions?.[key] === true;
    if (user?.isSuperadmin) return true;
    return effectivePermissions?.[key] === true;
  }, [user, simulatedUser, effectivePermissions]);

  const canWrite = (module) => hasPermission(`${module}.write`);
  const canRead  = (module) => hasPermission(`${module}.read`);

  return (
    <CampContext.Provider value={{
      camp, loading, error, modules, isModuleEnabled, refreshCamp, campSlug,
      tenant, allCampsInTenant, isActiveCamp,
      effectivePermissions, hasPermission, canWrite, canRead,
    }}>
      {children}
    </CampContext.Provider>
  );
}

export function useCamp() {
  const ctx = useContext(CampContext);
  if (!ctx) throw new Error('useCamp must be used within a CampProvider');
  return ctx;
}

export default CampContext;
